import { useEffect, useState } from 'react';
import { api, saveSession, type AuthResult } from '../lib/api.ts';
import { ChangePassword } from './ChangePassword.tsx';
import { LoginArt } from './LoginArt.tsx';

/**
 * 登录 —— 员工登录、普通登录两种入口。
 *
 * 员工登录（SSO 统一身份）：只放一个入口链接指向 /sso/login 独立页 ——
 * 自绘账密表单与跳转 Casdoor 都在那里，本页不承载 SSO 表单。
 * 普通登录用账号密码，账号由超管创建，本页不再提供自助注册。
 * 两种模式共用同一张卡片，.auth-mode 定高保证切换 tab 时卡片不跳。
 *
 * 用初始密码（如超管 admin）登录成功但不进系统：mustChangePassword 为 true 时
 * 原地切到改密表单 —— token 已存，改完密无缝进入。
 */
export function Login({ onAuthed }: { onAuthed: (r: AuthResult) => void }) {
  const [mode, setMode] = useState<'employee' | 'login'>('login');
  const [pending, setPending] = useState<AuthResult | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  useEffect(() => {
    api
      .ssoStatus()
      .then((r) => setSsoEnabled(r.enabled === true))
      .catch(() => setSsoEnabled(false));
  }, []);

  if (pending !== null) {
    return (
      <ChangePassword
        username={pending.user.username}
        onDone={() => {
          onAuthed(pending);
        }}
      />
    );
  }

  /** 登录成功后的公共出口：强制改密原地切表单，否则进系统。 */
  const settle = (r: AuthResult): void => {
    saveSession(r);
    if (r.user.mustChangePassword) {
      setPending(r);
    } else {
      onAuthed(r);
    }
  };

  const submitPassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      settle(await api.login({ username, password }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const tabs: readonly { readonly key: typeof mode; readonly label: string }[] = [
    { key: 'employee', label: '员工登录' },
    { key: 'login', label: '普通登录' },
  ];

  return (
    <div className="auth-page">
      {/* 宽屏时右侧线稿插画 —— 窄屏收起，表单永远优先 */}
      <LoginArt />
      <div className="auth-card">
        <div className="auth-head">
          <span className="auth-brand">UP-LAB</span>
          <span className="auth-sub">AI Native 部门的操作系统</span>
        </div>

        <div className="auth-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={mode === tab.key ? 'on' : ''}
              onClick={() => {
                setMode(tab.key);
                setError(null);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {mode === 'employee' ? (
          ssoEnabled ? (
            <div className="auth-mode">
              <a className="auth-sso-link" href="/sso/login">
                统一身份登录（员工 SSO）→
              </a>
            </div>
          ) : (
            <div className="auth-mode">
              <p className="auth-note">
                员工统一身份登录未启用，请使用普通登录或联系管理员配置 SSO。
              </p>
            </div>
          )
        ) : (
          <form className="auth-mode" onSubmit={(e) => void submitPassword(e)}>
            <label className="auth-field">
              <span>用户名</span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                placeholder="3-32 位字母、数字或下划线"
                required
              />
            </label>
            <label className="auth-field">
              <span>密码</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="至少 8 位"
                required
              />
            </label>

            {error !== null && <div className="auth-error">{error}</div>}

            <button className="auth-submit" disabled={busy}>
              {busy ? '处理中…' : '登录'}
            </button>
          </form>
        )}
        <p className="auth-note">
          遇到账号问题找部门负责人。这里只管数据的事，不管人的事。
        </p>
      </div>
    </div>
  );
}
